/**
 * Email Capture — AJAX subscription handler.
 *
 * Handles form submission for [email_capture id="X"] shortcodes.
 * Fetches a fresh nonce at submit time so cached pages always use a valid nonce.
 * Supports multiple capture instances on the same page.
 *
 * @file email-capture.js
 */
(() => {
  'use strict';

  const captureElements = document.querySelectorAll('[data-email-capture]');
  if (!captureElements.length) return;

  /**
   * Attaches the submit handler to a single email capture section.
   *
   * @param {HTMLElement} captureSection
   * @returns {void}
   */
  const initCaptureForm = (captureSection) => {
    const captureId      = captureSection.dataset.captureId;
    const successMessage = captureSection.dataset.successMsg;
    const form           = captureSection.querySelector('form');
    const fieldsWrapper  = captureSection.querySelector('[data-ec-fields]');
    const errorEl        = captureSection.querySelector('[data-ec-error]');
    const thanksEl       = captureSection.querySelector('[data-ec-thanks]');
    const submitButton   = form ? form.querySelector('[type="submit"]') : null;
    const honeypotInput  = form ? form.querySelector('input[name="honeypot_value"]') : null;

    if (!form || !fieldsWrapper || !errorEl || !thanksEl || !submitButton) return;

    /** @returns {void} */
    const showError = (message) => {
      errorEl.textContent = message;
      errorEl.classList.remove('tw-hidden');
    };

    /** @returns {void} */
    const clearError = () => {
      errorEl.textContent = '';
      errorEl.classList.add('tw-hidden');
    };

    /** @returns {void} */
    const showSuccess = () => {
      fieldsWrapper.classList.add('tw-hidden');
      thanksEl.textContent = successMessage;
      thanksEl.classList.remove('tw-hidden');
    };

    /**
     * Signals rate-predictor.js to reveal the gated result. The DOM reveal
     * lives in rate-predictor.js so the unlock logic has a single owner;
     * here we only persist the unlocked state and fire the event.
     *
     * @returns {void}
     */
    const unlockRatePredictor = () => {
      try {
        localStorage.setItem('annuity_rp_unlocked', '1');
      } catch {
        // Ignore storage failures; the current successful submission should still unlock.
      }

      document.dispatchEvent(new CustomEvent('ratePredictorUnlocked'));
    };

    /**
     * The shared form pipeline never sees this widget: no js-validateForm, its own fetch.
     * Without these calls it reports nothing at all.
     *
     * @param {string} action - Submit, Success or Error.
     * @param {string} [reason] - Failure detail. Reaches Mixpanel only; the Form category builds
     *                            its GA label from step/location/offer.
     */
    const trackCapture = (action, reason) => {
      if (typeof window.customEvent !== 'function') {
        return;
      }

      window.customEvent('Form', {
        action: action,
        step: 0,
        title: form.dataset.trackTitle,
        location: captureSection.closest('[data-location]')?.dataset.location || 'email-capture',
        offer: form.dataset.trackOffer || captureId,
        reason: reason,
        microIgnore: false,
        nonInteraction: false
      });
    };

    form.addEventListener('submit', async (submitEvent) => {
      submitEvent.preventDefault();

      const emailInput = form.querySelector('input[name="email"]');
      const emailValue = emailInput.value.trim();

      if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue)) {
        emailInput.setAttribute('aria-invalid', 'true');
        showError('Please enter a valid email address.');
        emailInput.focus();
        trackCapture('Error', 'Invalid Email');
        return;
      }

      emailInput.removeAttribute('aria-invalid');
      clearError();
      trackCapture('Submit');

      const originalButtonLabel = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Submitting…';

      const resetButton = () => {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.textContent = originalButtonLabel;
      };

      try {
        const nonceFormData = new FormData();
        nonceFormData.append('action', 'get_email_capture_nonce');
        const nonceResponse = await fetch(window.EmailCaptureConfig?.ajaxUrl, { method: 'POST', body: nonceFormData });
        const nonceResult   = await nonceResponse.json();

        const subscribeFormData = new FormData();
        subscribeFormData.append('action', 'email_capture_subscribe');
        subscribeFormData.append('nonce', nonceResult.data.nonce);
        subscribeFormData.append('email', emailValue);
        subscribeFormData.append('capture_id', captureId);
        subscribeFormData.append('source_url', window.location.href);
        subscribeFormData.append('honeypot_value', honeypotInput?.value ?? '');

        const subscribeResponse = await fetch(window.EmailCaptureConfig?.ajaxUrl, { method: 'POST', body: subscribeFormData });
        const subscribeResult   = await subscribeResponse.json();

        if (subscribeResult.success) {
          trackCapture('Success');
          showSuccess();
          if (captureSection.dataset.emailCaptureGate === 'rate-predictor') {
            unlockRatePredictor();
          }
        } else {
          resetButton();
          const failureMessage = subscribeResult.data?.message ?? 'Something went wrong. Please try again.';
          trackCapture('Error', failureMessage);
          showError(failureMessage);
        }
      } catch {
        resetButton();
        trackCapture('Error', 'Request Failed');
        showError('Something went wrong. Please try again.');
      }
    });
  };

  captureElements.forEach(initCaptureForm);
})();
